import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import MemoEditDialog from './MemoEditDialog.js'
import MemoDeleteDialog from './MemoDeleteDialog.js'
import { fetchMemos, errorMessage, formatDateTime } from './memoApi.js'

// 医師・病院の詳細ダイアログに埋め込むメモ欄
// personalCode を渡すとその医師のメモ、workplaceInstitutionCode のみを渡すとその病院のメモ
// （病院のメモには、その病院を併記した医師メモも含まれる）
// showAddButton=false: 追加ボタンを出さない（タイトルバーの MemoAddIconButton から追加する画面用）
// reloadKey: 値が変わると再読み込みする（外部で追加・更新したときに渡す）
// onChange: このメモ欄で編集・削除・追加したときに呼ぶ（タイトルバーの件数更新用）
export default function MemoSection(props) {
  const {
    token, personalCode, workplaceInstitutionCode, targetLabel, hospitalOptions,
    showAddButton = true, reloadKey, onChange,
  } = props;

  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadMemos = useCallback(() => {
    if (!personalCode && !workplaceInstitutionCode) {
      return;
    }

    const params = personalCode
      ? { personal_code: personalCode }
      : { workplace_institution_code: workplaceInstitutionCode };

    setLoading(true);
    setError('');

    fetchMemos(token, params)
      .then((response) => {
        setLoading(false);
        setMemos(response.data.rows);
      })
      .catch((err) => {
        setLoading(false);
        setError(errorMessage(err));
      });
  }, [token, personalCode, workplaceInstitutionCode]);

  useEffect(() => {
    loadMemos();
  }, [loadMemos, reloadKey]);

  const handleEditClose = (saved) => {
    setEditOpen(false);
    setEditTarget(null);
    if (saved) {
      loadMemos();
      if (onChange) {
        onChange();
      }
    }
  };

  const handleDeleteClose = (deleted) => {
    setDeleteTarget(null);
    if (deleted) {
      loadMemos();
      if (onChange) {
        onChange();
      }
    }
  };

  // 医師のメモ欄では病院名を、病院のメモ欄では医師名を補足として出す
  const subLabel = (memo) => (personalCode ? memo.formal_name : memo.doctor_name);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5">メモ</Typography>
        {showAddButton &&
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditTarget(null);
              setEditOpen(true);
            }}
          >
            追加
          </Button>
        }
        {loading && <CircularProgress size={20} />}
      </Stack>

      {error &&
        <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
      }

      {!loading && memos.length === 0 && !error &&
        <Typography color="text.secondary" sx={{ m: 1 }}>メモはまだありません</Typography>
      }

      <Stack spacing={1}>
        {memos.map((memo) => (
          <Paper key={memo.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {subLabel(memo) &&
                  <Typography variant="body2" color="primary" sx={{ mb: 0.5 }}>{subLabel(memo)}</Typography>
                }
                <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{memo.content}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {memo.author_name}　{formatDateTime(memo.updated_at)}
                </Typography>
              </Box>
              {memo.editable &&
                <>
                  <IconButton
                    size="small"
                    aria-label="編集"
                    onClick={() => {
                      setEditTarget(memo);
                      setEditOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="削除" onClick={() => setDeleteTarget(memo)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              }
            </Stack>
          </Paper>
        ))}
      </Stack>

      <MemoEditDialog
        open={editOpen}
        onClose={handleEditClose}
        token={token}
        memo={editTarget}
        personalCode={personalCode}
        workplaceInstitutionCode={workplaceInstitutionCode}
        targetLabel={targetLabel}
        hospitalOptions={hospitalOptions}
      />

      <MemoDeleteDialog
        open={Boolean(deleteTarget)}
        onClose={handleDeleteClose}
        token={token}
        memo={deleteTarget}
      />
    </Box>
  );
}

MemoSection.propTypes = {
  token: PropTypes.string.isRequired,
  personalCode: PropTypes.string,
  workplaceInstitutionCode: PropTypes.string,
  targetLabel: PropTypes.string,
  hospitalOptions: PropTypes.array,
  showAddButton: PropTypes.bool,
  reloadKey: PropTypes.number,
  onChange: PropTypes.func,
};
